import { z } from 'zod';
import { IdSchema, TimestampSchema } from './common.js';
import { ScopeLevelSchema } from './permissions.js';

/**
 * Invitations (doc 06 section 7).
 *
 * An administrator never chooses someone else's password. They create an
 * invitation carrying the role and scope; the invitee sets their own credential
 * on first use. That way no password ever exists in two places, and no admin is
 * in a position to know a user's credential.
 */

export const InvitationSchema = z.object({
  id: IdSchema,
  email: z.string().email(),
  full_name: z.string().nullable(),
  role_key: z.string(),
  role_name: z.string(),
  scope_level: ScopeLevelSchema,
  tenant_id: IdSchema.nullable(),
  tenant_name: z.string().nullable(),
  expires_at: TimestampSchema,
  accepted_at: TimestampSchema.nullable(),
  revoked_at: TimestampSchema.nullable(),
  created_at: TimestampSchema,
});
export type Invitation = z.infer<typeof InvitationSchema>;

export const InvitationCreateSchema = z.object({
  email: z.string().email().max(320),
  full_name: z.string().max(200).optional(),
  role_key: z.string().min(1).max(64),
  scope_level: ScopeLevelSchema.default('tenant'),
  tenant_id: IdSchema.optional(),
  reseller_id: IdSchema.optional(),
  /** Days until the link stops working. Short by default. */
  expires_in_days: z.number().int().min(1).max(30).default(7),
}).strict();
export type InvitationCreate = z.infer<typeof InvitationCreateSchema>;

/** What the accept page may learn BEFORE authentication. */
export const InvitationPreviewSchema = z.object({
  email: z.string().email(),
  full_name: z.string().nullable(),
  role_name: z.string(),
  scope_level: ScopeLevelSchema,
  tenant_name: z.string().nullable(),
  expires_at: TimestampSchema,
});

/**
 * Minimum 12 characters and nothing else (doc 06 section 6).
 *
 * No composition rules and no forced rotation: both push people towards
 * predictable patterns and password reuse, which is worse than a long
 * passphrase. Length is the property that actually resists guessing.
 */
export const PASSWORD_MIN_LENGTH = 12;

export const InvitationAcceptSchema = z.object({
  password: z.string().min(PASSWORD_MIN_LENGTH,
    `Password must be at least ${PASSWORD_MIN_LENGTH} characters`).max(512),
  full_name: z.string().min(1).max(200).optional(),
}).strict();
export type InvitationAccept = z.infer<typeof InvitationAcceptSchema>;
